import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { authHeader } from "../auth";

// Convertit un fichier image en data URL (base64), stocké tel quel — même
// principe que les logos de Config.jsx côté live SkiAlpPro.
function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function LogoField({ label, value, onChange }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {label}
            </Typography>
            {value && (
                <Box
                    component="img"
                    src={value}
                    alt={label}
                    sx={{ maxHeight: 80, maxWidth: 200, display: "block", mb: 1, border: "1px solid #eee" }}
                />
            )}
            <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    onChange(await fileToDataUrl(file));
                }}
            />
            {value && (
                <Button size="small" color="error" onClick={() => onChange("")} sx={{ ml: 1 }}>
                    Retirer
                </Button>
            )}
        </Box>
    );
}

const NEW_CATEGORY_VALUE = "__new__";

export default function ParallelConfig() {
    const { category: categoryFromUrl } = useParams();
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || "");
    const [newCategoryName, setNewCategoryName] = useState("");

    const [raceTitle, setRaceTitle] = useState("");
    const [subTitle, setSubTitle] = useState("");
    const [logoLeft, setLogoLeft] = useState("");
    const [logoRight, setLogoRight] = useState("");
    const [footerText, setFooterText] = useState("");
    const [footerLogo, setFooterLogo] = useState("");
    const [bannerImage, setBannerImage] = useState("");
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    // Liste des catégories existantes, pour le menu déroulant.
    useEffect(() => {
        fetch("/api/parallel-categories", { headers: { ...authHeader() } })
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => {
                const names = (list || []).map((c) => c.category);
                setCategories(names);
                // Si aucune catégorie n'est présélectionnée (pas d'URL) et
                // qu'il en existe au moins une, on sélectionne la première.
                if (!categoryFromUrl && names.length > 0) {
                    setSelectedCategory(names[0]);
                }
            })
            .catch(() => {});
    }, [categoryFromUrl]);

    // Charge la config de la catégorie sélectionnée.
    useEffect(() => {
        if (!selectedCategory || selectedCategory === NEW_CATEGORY_VALUE) {
            setRaceTitle("");
            setSubTitle("");
            setLogoLeft("");
            setLogoRight("");
            setFooterText("");
            setFooterLogo("");
            return;
        }

        fetch(`/api/parallel-branding/${encodeURIComponent(selectedCategory)}`, {
            headers: { ...authHeader() },
        })
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => {
                setRaceTitle(cfg.raceTitle || "");
                setSubTitle(cfg.subTitle || "");
                setLogoLeft(cfg.logoLeft || "");
                setLogoRight(cfg.logoRight || "");
                setFooterText(cfg.footerText || "");
                setFooterLogo(cfg.footerLogo || "");
                setBannerImage(cfg.bannerImage || "");
                setStatus("");
                setError("");
            })
            .catch((err) => setError(err.message));
    }, [selectedCategory]);

    const effectiveCategory =
        selectedCategory === NEW_CATEGORY_VALUE ? newCategoryName.trim() : selectedCategory;

    const save = async () => {
        setStatus("");
        setError("");

        if (!effectiveCategory) {
            setError("Choisis ou saisis une catégorie avant d'enregistrer.");
            return;
        }

        try {
            const res = await fetch(`/api/parallel-branding/${encodeURIComponent(effectiveCategory)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({
                    raceTitle,
                    subTitle,
                    logoLeft,
                    logoRight,
                    footerText,
                    footerLogo,
                    bannerImage,
                }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            setStatus("Enregistré ✓");

            // Si on venait de créer une nouvelle catégorie, on bascule
            // proprement dessus (URL + liste déroulante à jour).
            if (selectedCategory === NEW_CATEGORY_VALUE) {
                setCategories((prev) =>
                    prev.includes(effectiveCategory) ? prev : [...prev, effectiveCategory]
                );
                setSelectedCategory(effectiveCategory);
                setNewCategoryName("");
                navigate(`/parallel-config/${encodeURIComponent(effectiveCategory)}`, { replace: true });
            }
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <Box p={3} maxWidth={800}>
            <Typography variant="h4" mb={1}>
                Config impression — courses parallèles
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Habillage papier à en-tête utilisé sur les impressions du classement et du tableau des
                paires. Chaque course parallèle (women1, men1...) a sa propre config, indépendante des
                autres.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Catégorie à configurer</InputLabel>
                    <Select
                        label="Catégorie à configurer"
                        value={selectedCategory}
                        onChange={(e) => {
                            const value = e.target.value;
                            setSelectedCategory(value);
                            if (value !== NEW_CATEGORY_VALUE) {
                                navigate(`/parallel-config/${encodeURIComponent(value)}`, { replace: true });
                            }
                        }}
                    >
                        {categories.map((cat) => (
                            <MenuItem key={cat} value={cat}>
                                {cat}
                            </MenuItem>
                        ))}
                        <MenuItem value={NEW_CATEGORY_VALUE}>
                            <em>+ Nouvelle catégorie…</em>
                        </MenuItem>
                    </Select>
                </FormControl>

                {selectedCategory === NEW_CATEGORY_VALUE && (
                    <TextField
                        fullWidth
                        label="Nom de la nouvelle catégorie"
                        placeholder="ex: women2"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                )}
            </Paper>

            {effectiveCategory && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold" }}>
                        {effectiveCategory}
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LogoField
                                label="Bannière pleine largeur (affichée avant le titre, comme un bandeau)"
                                value={bannerImage}
                                onChange={setBannerImage}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titre de la course"
                                placeholder="ex: FIS TELEMARK WORLDCUP MELCHSEE-FRUTT"
                                value={raceTitle}
                                onChange={(e) => setRaceTitle(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Sous-titre (dates, lieu...)"
                                placeholder="ex: 23. & 24. Januar 2026"
                                value={subTitle}
                                onChange={(e) => setSubTitle(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <LogoField label="Logo gauche (en-tête)" value={logoLeft} onChange={setLogoLeft} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LogoField label="Logo droit (en-tête)" value={logoRight} onChange={setLogoRight} />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Texte de pied de page"
                                placeholder="ex: 321-timing.ch — résultats officiels"
                                value={footerText}
                                onChange={(e) => setFooterText(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <LogoField
                                label="Logo pied de page (pleine largeur)"
                                value={footerLogo}
                                onChange={setFooterLogo}
                            />
                        </Grid>
                    </Grid>

                    {error && (
                        <Typography color="error" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}
                    {status && (
                        <Typography color="success.main" sx={{ mt: 2 }}>
                            {status}
                        </Typography>
                    )}

                    <Button variant="contained" sx={{ mt: 3 }} onClick={save}>
                        Enregistrer
                    </Button>
                </Paper>
            )}
        </Box>
    );
}